import { styled, darken } from '@mui/material/styles';
import { AppBar, Button, Icon, IconButton, ListItemText, MenuItem, Tab, Tabs, Typography } from '@mui/material';
import Modal from 'global/components/modal';
import { isDark } from 'global/styles';

export const NAV_ACTIVE_CLASS = 'nav-active';

// PFM-2628: id of the header slot into which the mobile-only Share/Download/
// Print cluster is portaled (under the title, above the Location/Status row).
// Lives here (a leaf styles module) so both the NavBar header and the consumer
// (ReportTabs) can import it without a circular dependency.
export const ASSESSMENT_HEADER_ACTIONS_SLOT_ID = 'assessment-header-actions';

export const NavBarRoot = styled(AppBar, {
  shouldForwardProp: (p) => p !== 'navHidden',
})(({ theme, navHidden }) => ({
  flexGrow: 1,
  ...(!isDark(theme)
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }
    : {}),
  '@media print': {
    display: 'none',
  },
  '& h6': {
    width: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
  ...(navHidden ? { display: 'none' } : {}),
}));

export const NavBarContentRootWrap = styled('div')({
  flexGrow: 1,
  '@media print': {
    display: 'none',
  },
  '& h6': {
    width: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
});

export const NavBarContentRoot = styled(AppBar)(({ theme }) => {
  const chrome = isDark(theme) ? theme.palette.primary.dark : theme.palette.primary.main;

  return {
    background: `linear-gradient(to bottom, ${darken(chrome, 0.175)}, ${darken(chrome, 0.375)})`,
    color: theme.palette.getContrastText(chrome),
  };
});

export const NavBarContentActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

export const NavBarContentChildren = styled('div')({
  width: '100%',
});

// PFM-2628: container for the mobile-only Share/Download/Print cluster that is
// portaled in directly beneath the title (and above the Location/Status row).
// Spans full width so the right-aligned button cluster sits flush to the right
// edge of the header; the cluster's own styles hide it at `md`+.
export const NavBarContentActionsRow = styled('div')(({ theme }) => ({
  width: '100%',
  // box-sizing keeps the horizontal padding INSIDE the 100% width so the row
  // doesn't overflow the viewport (which pushed the right-side margin off-screen
  // and left the button cluster flush to the screen edges). PFM-2628.
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const NavBarGrow = styled('div')({
  flexGrow: 1,
});

export const NavBarMenuButton = styled(IconButton, {
  shouldForwardProp: (p) => p !== 'sidebar',
})(({ theme, sidebar }) => ({
  marginLeft: -12,
  ...(sidebar
    ? {}
    : {
        [theme.breakpoints.up('sm')]: {
          marginRight: 20,
        },
      }),
}));

export const NavBarTaskButton = styled('div')(({ theme }) => ({
  height: theme.spacing(2),
  minWidth: theme.spacing(16),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    minWidth: theme.spacing(8),
  },
}));

export const NavBarTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  textTransform: 'capitalize',
  display: 'none',
  [theme.breakpoints.between('sm', 'md')]: {
    display: 'block',
  },
}));

export const NavHeaderLogoWrapper = styled('a')({
  width: '100%',
  height: 120,
  display: 'flex',
  alignItems: 'center',
  // PFM-2625: center the logo horizontally within the drawer header. Builder
  // logos (e.g. Toll Brothers wordmark, round Redwood mark) sized themselves to
  // the top-left corner because the wrapper was shrink-to-content with no
  // horizontal justification; full width + centered justify restores the
  // centered IBACOS-style alignment for logos of any aspect ratio.
  justifyContent: 'center',
  // The logo renders through EImage (global/components/image), which wraps the
  // <img> in a fixed-width `.IMAGE` container holding an inner flex `> div`.
  // That inner div has no horizontal justification (global default flex-start),
  // so the visible <img> was pinned to the left of the .IMAGE container even
  // though the container itself was centered in this wrapper. Scope the centering
  // to this drawer logo only (do NOT touch the global EImage styles): stretch the
  // container to full width and center its inner flex content so the actual image
  // sits in the middle of the drawer header for any aspect ratio.
  '& .IMAGE > div': {
    justifyContent: 'center',
    // The inner EImage div carries a global horizontal margin (spacing) that made
    // it overflow the full-width container and shift the centered image ~8px right.
    // Zero the horizontal margin so the visible image is truly centered.
    marginLeft: 0,
    marginRight: 0,
  },
  // PFM-2625 follow-up: the rendered drawer logo <img> (EImage renders it under
  // the `.IMAGE` container) carries inline width:100%/height:100% with the
  // browser-default object-fit:fill. The `.IMAGE` box constrains width (200px)
  // and height (100px) on INDEPENDENT axes, so a wide wordmark (e.g. Pulte,
  // Conner Homes — natural ~6:1) gets stretched to fill the ~2:1 box and looks
  // squished. Force object-fit:contain on just this drawer logo image so it
  // scales down within the box preserving aspect ratio (e.g. 200×~33, centered)
  // instead of filling it. Scoped to the NavBar drawer — the global EImage
  // styles are untouched.
  '& .IMAGE img': {
    objectFit: 'contain',
  },
});

export const NavHeaderLogo = styled('img')(({ theme }) => ({
  margin: theme.spacing(1),
  maxHeight: '100px',
  maxWidth: '200px',
}));

export const NavPerformLogo = styled('img')({
  margin: '24px auto',
  maxWidth: '100px',
});

export const NavCopyright = styled('div')(({ theme }) => ({
  display: 'block',
  fontSize: '11px',
  margin: 'auto',
  marginTop: theme.spacing(2),
}));

export const NavMenuTitle = styled(ListItemText)({
  textTransform: 'capitalize',
});

// Active state is applied via NavLink activeClassName={NAV_ACTIVE_CLASS}.
export const NavMenuItem = styled(MenuItem)(({ theme }) => ({
  [`&.${NAV_ACTIVE_CLASS}`]: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 'bold',
  },
}));

export const NavLocationButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
}));

export const NavLocationIcon = styled(Icon)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

export const NavTabIcon = styled('span')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'inline-flex',
  alignItems: 'center',
}));

export const NavTabs = styled(Tabs)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const NavTab = styled(Tab)({
  flexDirection: 'row',
  display: 'flex',
  alignItems: 'center',
  color: 'inherit',
  opacity: 0.75,
  '&.Mui-selected': {
    color: 'inherit',
    opacity: 1,
  },
});

export const NavResetButton = styled(Button)(({ theme }) => ({
  minWidth: 'inherit',
  width: theme.spacing(5),
}));

export const NavNewLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const NavRealmModal = styled(Modal)({
  '& .MuiDialog-paperScrollPaper': {
    height: '100%',
  },
});
